import axios from '@/plugins/axios'

const state = {
    blobs: [],
    currentBlobId: 0,
    highlightBlobId: 0,
    ghostBlob: null,
    latestBlobsUpdate: null
}

// getters
const getters = {
    /**
     * get blobs from radar
     * @param state
     * @returns {function(...[*]=)}
     */
    getRadarBlobs(state) {
        return (id) => {
            if(state.blobs.length > 0)
                return state.blobs.filter(b => b.radar_id === id)
            else
                return []
        }
    },

    getCurrentBlobId (state) {
        return state.currentBlobId
    },

    getCurrentBlob(state) {
        if(state.currentBlobId === 0)
            return {}

        return state.blobs.find(b => b.id == state.currentBlobId)
    },

    getHighlightBlobId(state) {
        return state.highlightBlobId
    },

    getGhostBlob(state) {
        return state.ghostBlob
    },

    getLatestBlobUpdate(state) {
        return state.latestBlobsUpdate
    },

    getLatestBlobs(state) {
        return state.blobs.slice(0, 10);
    }
}

// actions
const actions = {
    /**
     * load blobs from radar
     * @param commit
     * @param radarId
     * @returns {Promise<T>}
     */
    async loadRadarBlobs({ commit }, radarId) {
        commit('addToLoadingQueue', 'load_radar_blobs' + radarId,  { root: true });

        return axios.get('/blob/' + radarId)
            .then((response) => {
                commit('setRadarBlobs', response.data );
                commit('removeFromLoadingQueue', 'load_radar_blobs' + radarId,  { root: true });
            })
            .catch((error) => {
                commit('removeFromLoadingQueue', 'load_radar_blobs' + radarId,  { root: true });
                commit('error/newError', {
                    title: 'error.blob.load',
                    text: error.response? error.response.data.message || error.message: error.message
                }, { root: true});
            });
    },

    /**
     * load latest blobs
     * @param commit
     * @returns {Promise<*>}
     */
    async loadLatestBlobs({ commit }) {
        commit('addToLoadingQueue', 'load_latest_blobs',  { root: true });

        return axios.get('/blob')
          .then((response) => {
              commit('setRadarBlobs', response.data );
              commit('setUpdateTime');
              commit('removeFromLoadingQueue', 'load_latest_blobs',  { root: true });
          })
          .catch((error) => {
              commit('removeFromLoadingQueue', 'load_latest_blobs',  { root: true });
              commit('error/newError', {
                  title: 'error.blob.load',
                  text: error.response? error.response.data.message || error.message: error.message
              }, { root: true});
          });
    },

    /**
     * create blob
     * @param commit
     * @param blob
     * @returns {Promise<unknown>}
     */
    async createBlob({ commit }, blob) {
        // set loader
        commit('addToLoadingQueue', 'blob_create_new',  { root: true });

        return new Promise((resolve, reject) => {
            // send create request
            axios.post('/blob', blob)
                .then((response) => {
                    // update radar
                    commit('setBlob', response.data.blob)

                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_create_new', { root: true });

                    resolve(response);
                })
                .catch((error) => {
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_create_new', { root: true });
                    // raise error
                    commit('error/newError', {
                        title: 'error.blob.create',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    },

    /**
     * create blob entry
     * @param commit
     * @param blob
     * @returns {Promise<unknown>}
     */
    async createBlobEntry({ commit }, entry) {
        // set loader
        commit('addToLoadingQueue', 'blob_entry_create_new',  { root: true });

        return new Promise((resolve, reject) => {
            // send create request
            axios.post('/entry', entry )
                .then((response) => {
                    // update blob entry
                    commit('setBlobEntry', response.data.entry)

                    // update blob
                    commit('updateBlobPosition', { blob_id: entry.blob_id, sector: entry.sector, sector_progress: entry.sector_progress })


                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_entry_create_new', { root: true });

                    resolve(response);
                })
                .catch((error) => {
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_entry_create_new', { root: true });
                    // raise error
                    commit('error/newError', {
                        title: 'error.entry.create',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    },


    /**
     * update blob
     * @param commit
     * @param blob
     * @returns {Promise<unknown>}
     */
    async updateBlob({ commit }, blob) {
        // set loader
        commit('addToLoadingQueue', 'blob_update_' + blob.id, { root: true });

        return new Promise((resolve, reject) => {
            // send update request
            axios.put('/blob/' + blob.id, blob)
                .then((response) => {
                    // update blob
                    commit('setBlob', response.data.blob)
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_update_' + blob.id, {root: true});
                    resolve(response);
                })
                .catch((error) => {
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_update_' + blob.id, {root: true});
                    // raise error
                    commit('error/newError', {
                        title: 'error.blob.update',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    },

    /**
     * update blob entry
     * @param commit
     * @param entry
     * @returns {Promise<unknown>}
     */
    async updateBlobEntry({ commit }, entry) {
        // set loader
        commit('addToLoadingQueue', 'blob_entry_update_' + entry.id, { root: true });

        return new Promise((resolve, reject) => {
            // send update request
            axios.put('/entry/' + entry.id, entry)
                .then((response) => {
                    // update blob entry
                    commit('setBlobEntry', response.data.entry)

                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_entry_update_' + entry.id, {root: true});
                    resolve(response);
                })
                .catch((error) => {
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_entry_update_' + entry.id, {root: true});
                    // raise error
                    commit('error/newError', {
                        title: 'error.entry.update',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    },

    /**
     * remove blob
     * @param commit
     * @param blob
     * @returns {Promise<unknown>}
     */
    async removeBlob({ commit }, blob) {
        // set loader
        commit('addToLoadingQueue', 'blob_delete_' + blob.id, { root: true });

        return new Promise((resolve, reject) => {
            // send update request
            axios.delete('/blob/' + blob.id)
                .then((response) => {

                    // update radar
                    commit('removeBlob', blob);
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_delete_' + blob.id, {root: true});

                    resolve(response);
                })
                .catch((error) => {
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_delete_' + blob.id, {root: true});

                    commit('error/newError', {
                        title: 'error.blob.delete',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    },

    /**
     * remove blob entry
     * @param commit
     * @param entry
     * @returns {Promise<unknown>}
     */
    async removeBlobEntry({ commit }, entry) {
        // set loader
        commit('addToLoadingQueue', 'blob_entry_delete_' + entry.id, { root: true });

        return new Promise((resolve, reject) => {
            // send update request
            axios.delete('/entry/' + entry.id   )
                .then((response) => {

                    // update radar
                    commit('removeBlobEntry', entry);
                    // remove loader
                    commit('removeFromLoadingQueue', 'blob_entry_delete_' + entry.id, {root: true});

                    resolve(response);
                })
                .catch((error) => {
                    commit('removeFromLoadingQueue', 'blob_entry_delete_' + entry.id, {root: true});

                    commit('error/newError', {
                        title: 'error.entry.delete',
                        text: error.response? error.response.data.message || error.message: error.message
                    }, { root: true});

                    reject(error);
                })
        });
    }
}

// mutations
const mutations = {

    /**
     * set radar blobs
     * @param state
     * @param radarId
     * @param blobs
     */
    setRadarBlobs(state, blobs) {
        if(state.blobs.length > 0) {
            for (const i in blobs) {
                let pos = state.blobs.findIndex(b => b.id == blobs[i].id);

                if(pos <= -1) {
                    state.blobs.push(blobs[i]);
                } else {
                    // need to assign object to keep state reactive
                    state.blobs[pos] = Object.assign(state.blobs[pos], blobs[i]);
                }
            }

        } else {
            state.blobs = blobs
        }
    },

    /**
     * set current blob
     * @param state
     * @param radarId
     * @param blobId
     */
    setCurrentBlobId(state, blobId ) {
        state.currentBlobId = blobId
    },

    setHighlightBlobId(state, blobId) {
        state.highlightBlobId = blobId
    },

    setGhostBlob(state, ghostBlob) {
        // get all blobs from ghosBlob quadrant
        let quadrantBlobs = state.blobs.filter(b => b.quadrant === ghostBlob.quadrant)
        // find index position of ghostBlob (needed for positioning)
        ghostBlob.pos = quadrantBlobs.findIndex(b => b.id === ghostBlob.blob_id)

        state.ghostBlob = ghostBlob
    },

    clearCurrentBlobId(state) {
        state.currentBlobId = 0
    },

    clearHighlightBlobId(state) {
        state.highlightBlobId = 0
    },

    clearGhostBlob(state) {
        state.ghostBlob = null
    },

    /**
     * set/update blob
     * @param state
     * @param blob
     */
    setBlob(state, blob) {

        let pos = state.blobs.findIndex(b => b.id == blob.id)

        if(pos <= -1) {
            state.blobs.push(blob)
        } else {
            // replace object and stay reactive
            state.blobs.splice(pos, 1, blob)
        }
    },

    updateBlobPosition(state, sectorData) {
        let pos = state.blobs.findIndex(b => b.id == sectorData.blob_id)

        if(pos > -1) {
            // replace object and stay reactive
            state.blobs.splice(pos, 1, Object.assign(state.blobs[pos], { sector: sectorData.sector, sector_progress: sectorData.sector_progress }))
        }
    },

    /**
     * set/update entry
     * @param state
     * @param entry
     */
    setBlobEntry(state, entry) {
        let blobPos = state.blobs.findIndex(b => b.id == entry.blob_id);
        let pos = state.blobs[blobPos].entries.findIndex(e => e.id == entry.id);

        if(pos <= -1) {
            state.blobs[blobPos].entries.unshift(entry);
        } else {
            // need to assign object to keep state reactive
            state.blobs[blobPos].entries[pos] = Object.assign(state.blobs[blobPos].entries[pos], entry);
        }
    },

    /**
     * remove blob from state
     * @param state
     * @param blob
     */
    removeBlob(state, blob) {
        let pos = state.blobs.findIndex(b => b.id == blob.id);

        if(pos > -1) {
            state.blobs.splice(pos, 1);
        }
    },

    /**
     * remove blob entry from state
     * @param state
     * @param entry
     */
    removeBlobEntry(state, entry) {
        let blob = state.blobs.find(b => b.id == entry.blob_id);
        let pos =  blob.entries.findIndex(e => e.id == entry.id);

        if(pos > -1) {
            blob.entries.splice(pos, 1);
        }
    },

    setUpdateTime(state) {
        state.latestBlobsUpdate = Date.now()
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
